import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { investmentsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Bitcoin, Loader2, CheckCircle, Pencil, Search } from 'lucide-react';
import { INVESTMENT_STATUS, CRYPTO_OPTIONS, fmtDate, fmtEur, InvestmentStatusBadge, CryptoChip } from '../../components/investment/investmentShared';
import { PaymentReviewDialog, PaymentCell } from '../../components/investment/PaymentReview';
import { OpportunitiesManager } from '../../components/investment/OpportunitiesManager';

const AdminInvestmentsPage = () => {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [totals, setTotals] = useState({});
    const [tab, setTab] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [edit, setEdit] = useState(null);
    const [status, setStatus] = useState('pending');
    const [note, setNote] = useState('');
    const [notify, setNotify] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [payReview, setPayReview] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await investmentsAPI.adminList();
            setItems(data.requests || []);
            setCounts(data.counts || {});
            setTotals(data.totals || {});
        } catch {
            toast.error('Error al cargar solicitudes de inversión');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openEdit = (r) => { setEdit(r); setStatus(r.status); setNote(r.admin_note || ''); setNotify(true); };

    const confirm = async () => {
        setSubmitting(true);
        try {
            await investmentsAPI.adminSetStatus(edit.id, { status, admin_note: note, notify });
            toast.success(`Solicitud actualizada a «${INVESTMENT_STATUS[status].label}»`);
            setEdit(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar la solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    const q = search.trim().toLowerCase();
    const visible = items.filter((r) => (tab === 'all' || (tab === 'payments' ? r.payment?.status === 'submitted' : r.status === tab)) &&
        (!q || r.user_name?.toLowerCase().includes(q) || r.user_email?.toLowerCase().includes(q)));
    const totalAll = items.length;

    return (
        <Layout>
            <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-investments-page">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30">
                        <Bitcoin className="w-6 h-6 text-amber-300" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Solicitudes de Inversión</h1>
                        <p className="text-sm text-slate-400 font-light">Importes apartados por los usuarios en BTC y USDT TRC20 · Gestiona el estado de cada solicitud</p>
                    </div>
                </div>

                <OpportunitiesManager />

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CRYPTO_OPTIONS.map((c) => (
                        <div key={c.key} className="rounded-2xl bg-slate-900/70 border border-slate-800 px-4 py-3" data-testid={`admin-investments-total-${c.key}`}>
                            <p className="text-slate-400 text-xs flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.label} {c.sub}</p>
                            <p className="text-white text-lg font-bold mt-0.5">{fmtEur(totals[c.key]?.total)}</p>
                            <p className="text-slate-500 text-[11px]">{totals[c.key]?.count || 0} solicitudes</p>
                        </div>
                    ))}
                    {['pending', 'reserved'].map((k) => (
                        <div key={k} className="rounded-2xl bg-slate-900/70 border border-slate-800 px-4 py-3" data-testid={`admin-investments-count-${k}`}>
                            <p className="text-slate-400 text-xs">{INVESTMENT_STATUS[k].label}</p>
                            <p className="text-white text-lg font-bold mt-0.5">{counts[k] || 0}</p>
                            <p className="text-slate-500 text-[11px]">de {totalAll} en total</p>
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => setTab('all')}
                        className={`px-3.5 py-2 rounded-xl border text-sm font-semibold transition-colors ${tab === 'all' ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:text-white'}`}
                        data-testid="admin-investments-tab-all">Todas <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 ml-1">{totalAll}</span></button>
                    {Object.entries(INVESTMENT_STATUS).map(([k, st]) => (
                        <button key={k} onClick={() => setTab(k)}
                            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold transition-colors ${tab === k ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:text-white'}`}
                            data-testid={`admin-investments-tab-${k}`}>
                            <st.Icon className="w-4 h-4" /> {st.label}
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300">{counts[k] || 0}</span>
                        </button>
                    ))}
                    <button onClick={() => setTab('payments')}
                        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold transition-colors ${tab === 'payments' ? 'bg-violet-500/20 border-violet-400/60 text-white' : 'bg-slate-900/50 border-violet-500/30 text-violet-200 hover:text-white'}`}
                        data-testid="admin-investments-tab-payments">
                        Pagos por verificar
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300">{items.filter((r) => r.payment?.status === 'submitted').length}</span>
                    </button>
                    <div className="relative ml-auto w-full sm:w-64">
                        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar usuario o correo"
                            className="pl-9 bg-slate-950 border-slate-800 text-white text-sm" data-testid="admin-investments-search" />
                    </div>
                </div>

                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-amber-300 animate-spin" /></div>
                    ) : visible.length === 0 ? (
                        <p className="text-slate-600 text-sm text-center py-12" data-testid="admin-investments-empty">Sin solicitudes en esta vista.</p>
                    ) : (
                        <div className="divide-y divide-slate-800/60">
                            {visible.map((r) => (
                                <div key={r.id} className="px-4 py-3.5 flex flex-wrap items-center gap-3" data-testid={`admin-investment-${r.id}`}>
                                    <div className="flex-1 min-w-[240px]">
                                        <p className="text-white text-sm font-semibold">{fmtEur(r.amount)} · {r.user_name}</p>
                                        <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                            <CryptoChip crypto={r.crypto} />
                                            <span className="text-slate-500 text-xs">· {r.user_email} · {fmtDate(r.created_at)}</span>
                                        </div>
                                        {r.opportunity_name && <p className="text-[#f3e3a6] text-xs mt-0.5">Oportunidad: {r.opportunity_name}</p>}
                                        {r.note && <p className="text-slate-400 text-xs mt-0.5">Comentario del usuario: {r.note}</p>}
                                        {r.admin_note && <p className="text-amber-200/90 text-xs mt-0.5">Nota interna/usuario: {r.admin_note}</p>}
                                        {r.reviewed_by && <p className="text-slate-600 text-[10.5px] mt-0.5">Actualizado por {r.reviewed_by} · {fmtDate(r.reviewed_at)}</p>}
                                        {r.payment && <p className="text-violet-200/80 text-[10.5px] mt-0.5 font-mono break-all">TX: {r.payment.tx_hash}</p>}
                                    </div>
                                    <InvestmentStatusBadge status={r.status} testId={`admin-investment-status-${r.id}`} />
                                    <PaymentCell request={r} onReview={setPayReview} />
                                    <Button size="sm" onClick={() => openEdit(r)}
                                        className="bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 h-8" data-testid={`admin-investment-edit-${r.id}`}>
                                        <Pencil className="w-3.5 h-3.5 mr-1" /> Gestionar
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <PaymentReviewDialog request={payReview} onClose={() => setPayReview(null)} onChanged={load} />

                <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
                    <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white" data-testid="admin-investment-dialog">
                        <DialogHeader><DialogTitle className="text-white">Gestionar solicitud</DialogTitle></DialogHeader>
                        {edit && (
                            <div className="space-y-4 py-1">
                                <p className="text-slate-400 text-sm">{edit.user_name} · <span className="text-white font-semibold">{fmtEur(edit.amount)}</span> · <CryptoChip crypto={edit.crypto} /></p>
                                <div className="space-y-1.5">
                                    <p className="text-slate-400 text-xs">Estado</p>
                                    <div className="grid grid-cols-1 gap-2">
                                        {Object.entries(INVESTMENT_STATUS).map(([k, st]) => (
                                            <button key={k} type="button" onClick={() => setStatus(k)}
                                                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${status === k ? st.cls : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-white'}`}
                                                data-testid={`admin-investment-status-option-${k}`} aria-pressed={status === k}>
                                                <st.Icon className="w-4 h-4" /> {st.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-slate-400 text-xs">Nota para el usuario (opcional)</p>
                                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: Reserva confirmada, le contactaremos para la entrada"
                                        className="bg-slate-950 border-slate-800 text-white text-sm min-h-[80px]" data-testid="admin-investment-note-input" />
                                </div>
                                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-amber-400" data-testid="admin-investment-notify-checkbox" />
                                    Avisar al usuario por correo y notificación
                                </label>
                                {status === 'reserved' && (
                                    <p className="text-[11px] text-slate-400 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid="admin-investment-wallet-hint">
                                        Al marcar como Reservada, el usuario verá la wallet de pago <span className="text-white">{edit.crypto === 'BTC' ? 'BTC' : 'USDT TRC20'}</span> configurada en Administración › Wallets, con QR y dirección para financiarla.
                                    </p>
                                )}
                            </div>
                        )}
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setEdit(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancelar</Button>
                            <Button onClick={confirm} disabled={submitting} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="admin-investment-confirm-btn">
                                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                                Guardar
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default AdminInvestmentsPage;
